import { useState } from 'react'
import { ArrowLeftRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { eventActivitiesNoun } from './eventActivityNoun'

type Props = {
  event: wowaudit.Event
}

const scopeLabel = (scope: wowaudit.Event['raidsPlanningScope']) =>
  scope === 'single' ? 'single' : 'multi'

const EventSetupModeControl = ({ event }: Props) => {
  const { updateEvent, setAllActivitiesActive } = useTeamEventUpdateContext()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const activityNoun = eventActivitiesNoun(event.activities)

  const targetScope: wowaudit.Event['raidsPlanningScope'] =
    event.raidsPlanningScope === 'single' ? 'per_encounter' : 'single'

  const handleSwitch = async () => {
    setIsSwitching(true)
    try {
      await updateEvent({ raidsPlanningScope: targetScope })
      if (
        targetScope === 'single' &&
        !event.activities.some((activity) => activity.active)
      ) {
        await setAllActivitiesActive(true)
      }
      setIsDialogOpen(false)
    } finally {
      setIsSwitching(false)
    }
  }

  return (
    <>
      <button
        type='button'
        onClick={() => setIsDialogOpen(true)}
        className='group flex items-center gap-2 rounded-md border border-transparent px-2 py-1 text-sm text-muted-foreground transition-colors hover:border-border hover:bg-accent'
        aria-label='Switch setup mode'
      >
        <span>Setup mode:</span>
        <span className='font-semibold capitalize text-foreground'>
          {scopeLabel(event.raidsPlanningScope)}
        </span>
        <ArrowLeftRight className='h-3.5 w-3.5 opacity-60 transition-opacity group-hover:opacity-100' />
      </button>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (isSwitching) return
          setIsDialogOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch setup mode</DialogTitle>
          </DialogHeader>
          <p>
            In single setup mode, members can only see one setup, either for the
            entire event or for a specific {activityNoun.singular} that you
            select.
          </p>

          <p>
            In multi setup mode, you can create a different setup for each{' '}
            {activityNoun.singular}. Members will be able to see all of them.
          </p>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              disabled={isSwitching}
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              loading={isSwitching}
              className='w-40'
              onClick={() => void handleSwitch()}
            >
              Switch to {scopeLabel(targetScope)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default EventSetupModeControl
